/** @jsx jsx */
/* eslint-disable jsx-a11y/label-has-associated-control, max-len, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import Icon from 'components/atoms/Icon';
import { copyShifts, getBoard } from 'api/shiftBoard';
import {
  BoardData, GeneratedOperations, Operation, ShiftItem, StaffInfo,
} from 'domain/attend/shiftBoard/types';
import {
  addDays, daysBetweenInclusive, DOW_LABELS, parseDateStr,
} from 'domain/attend/shiftBoard/time';
import { holidayLook, mock } from './styles';
import BoardDatePicker from './BoardDatePicker';
import BoardSelect from './BoardSelect';

export type CopyModalProps = {
  open: boolean;
  orgCode: string;
  dateFrom: string;
  dateTo: string;
  staffs: Array<StaffInfo>;
  onClose: () => void;
  onGenerated: (result: GeneratedOperations) => void;
};

const SKIP_LABEL: { [k: string]: string } = {
  LOCKED: 'ロック中',
  AWAY_SHIFT: '他店支援あり',
  ACHIEVEMENT_EXISTS: '実績あり',
  SHIFT_EXISTS: '登録済み',
  MULTIPLE_SHIFTS: '1 日に複数のシフトあり',
  // 元のセルに 4 件以上ある（旧画面で入れたもの）。保存で 1 セル 3 件の決まりに当たるのでコピーしない（10/5）
  TOO_MANY_SHIFTS: '4 件以上あり（1 セル 3 件まで）',
};

/** 選べるコピー日数 */
const DAY_OPTIONS = [7, 14, 15, 16, 28, 30, 31];

const NAME_W = 120;
const COL_W = 96;

const styles = {
  section: css({
 fontSize: '13px', fontWeight: 700, color: mock.textMain, margin: '0 0 8px',
}),
  panel: css({
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    background: '#fff',
    padding: '16px 20px',
    marginBottom: '20px',
  }),
  label: css({
 fontSize: '12px', fontWeight: 700, color: mock.textMain, display: 'block', marginBottom: '6px',
}),
  row: css({
 display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap',
}),
  // 行は flex-end で並ぶ。ラベルを含めた高さの中央ではなく、入力欄の中央にそろえる
  arrow: css({
    alignSelf: 'flex-end',
    height: '34px',
    display: 'inline-flex',
    alignItems: 'center',
    color: mock.gray40,
    fontSize: '14px',
    padding: '0 2px',
  }),
  previewButton: css({
    height: '34px',
    padding: '0 16px',
    border: 'none',
    borderRadius: '4px',
    background: mock.primary,
    color: '#fff',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:disabled': { background: mock.primaryM80, cursor: 'default' },
  }),
  guideToggle: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: 0,
    color: mock.primary,
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }),
  guideLead: css({ fontSize: '12px', color: mock.textMain, margin: '10px 0 12px' }),
  guideCards: css({
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px', marginBottom: '20px',
  }),
  guideCard: css({
    border: `1px solid ${mock.gray10}`, borderRadius: '4px', padding: '12px', background: '#fff',
  }),
  guideTitle: css({
    fontSize: '12px', fontWeight: 700, textAlign: 'center', lineHeight: 1.6, marginBottom: '10px',
  }),
  guideBody: css({
 display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
}),
  guideNote: css({
    fontSize: '11px', lineHeight: 1.5, background: '#F5F5F5', color: mock.textSub, borderRadius: '3px', padding: '6px 8px', maxWidth: '150px',
  }),
  sample: css({
    width: '104px', minHeight: '54px', border: `1px solid ${mock.gray10}`, borderRadius: '2px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', fontSize: '12px',
  }),
  grid: css({
    // ヘッダーと本体で padding が違うため、border-box に統一しないと 1 列ずつ幅がずれていく
    '& *': { boxSizing: 'border-box' },
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '420px',
    // 画面が狭いときはモーダルの中で横スクロールさせる（モーダルそのものは画面に収める）
    maxWidth: '100%',
  }),
  gridRow: css({ display: 'flex', borderBottom: `1px solid ${mock.gray10}` }),
  headCell: css({
    flex: 'none',
    width: `${COL_W}px`,
    background: mock.primaryM95,
    borderLeft: `1px solid ${mock.gray10}`,
    textAlign: 'center',
    padding: '4px 0',
    fontSize: '11px',
    fontWeight: 700,
    position: 'sticky',
    top: 0,
  }),
  nameCell: css({
    flex: 'none',
    width: `${NAME_W}px`,
    padding: '6px 8px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#fff',
    position: 'sticky',
    left: 0,
    zIndex: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  cell: css({
    flex: 'none',
    width: `${COL_W}px`,
    minHeight: '46px',
    borderLeft: `1px solid ${mock.gray10}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1px',
    fontSize: '11px',
    padding: '3px 2px',
  }),
  before: css({ color: mock.gray40, lineHeight: 1.3 }),
  after: css({ color: mock.textMain, fontWeight: 700, lineHeight: 1.3 }),
  down: css({ color: mock.gray40, fontSize: '9px', lineHeight: 1 }),
  summary: css({
    display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', margin: '12px 0 8px',
  }),
  resetButton: css({
    height: '28px',
    padding: '0 12px',
    border: `1px solid ${mock.gray20}`,
    borderRadius: '4px',
    background: '#fff',
    color: mock.textMain,
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { background: mock.primaryM95, borderColor: mock.primaryM80 },
    '&:disabled': {
 color: mock.gray40, cursor: 'default', background: '#fff', borderColor: mock.gray10,
},
  }),
  skips: css({
 fontSize: '11px', color: mock.textSub, marginTop: '8px', maxHeight: '110px', overflowY: 'auto',
}),
};

/** セルの背景。白 = 変化なし、黄 = 変わる、グレー = 適用しない */
const SAME_BG = '#fff';
const DIFF_BG = '#FDF6E3';
const OFF_BG = '#EFEFEF';

const cellKey = (staffCode: string, date: string): string => `${staffCode}|${date}`;

/** 日曜は赤、土曜は青 */
const dowColor = (dow: number): string => {
  if (dow === 0) {
    return mock.secondary;
  }
  return dow === 6 ? mock.primary : mock.textSub;
};

/** 白 = 変化なし、グレー = 適用しない、黄 = 変わる */
const cellBackground = (changed: boolean, off: boolean): string => {
  if (!changed) {
    return SAME_BG;
  }
  return off ? OFF_BG : DIFF_BG;
};

const cellHint = (changed: boolean, off: boolean): string => {
  if (!changed) {
    return '';
  }
  return off ? '押すと適用します' : '押すと適用しません';
};

/**
 * シフト 1 件の表示。パターン名ではなく時間で見せる（一致しているかを見るため）
 * distinguish は、そのスタッフが法定／所定を区別するか。区別しない人の公休は保存すると必ず法定になるので、
 * 法定／所定の違いだけでは「変わった」にしない（盤面のカードと同じく「公休」と出す。10/6）
 */
const shiftLabel = (s: { start: string | null; end: string | null; isEndNextDay?: boolean; attendType?: number; isLegal?: boolean | null }, distinguish: boolean): string => {
  if (s.attendType !== undefined && s.attendType !== 0) {
    return holidayLook(s.isLegal, distinguish).short;
  }
  if (!s.start || !s.end) {
    return '';
  }
  return `${s.start}-${s.end}${s.isEndNextDay ? '翌' : ''}`;
};

const opLabel = (op: Operation, distinguish: boolean): string => shiftLabel({
  start: op.start || null, end: op.end || null, isEndNextDay: op.isEndNextDay, attendType: op.attendType, isLegal: op.isLegal,
}, distinguish);

/**
 * 前期間からコピー（C3_PARTNER-358。9/17 のモックに合わせて作り直し）
 *
 * 1. コピー日数・コピー元の開始日・適用先の開始日を決める
 * 2. プレビューで「既存シフト（上段）」と「反映予定シフト（下段）」を並べて見せる
 *    - 一致: 背景は白。反映予定は出さない（変わらないので）
 *    - 異なる: 背景は黄。両方出す
 *    - 適用しない: セルを押すと背景がグレーになり、その 1 件は積まない
 * 3. 選んだぶんだけシフト表に取り込む（取り込んだ時点で保存される。9/24〜）
 */
const CopyModal: React.FC<CopyModalProps> = ({
  open, orgCode, dateFrom, dateTo, staffs, onClose, onGenerated,
}) => {
  const viewDays = daysBetweenInclusive(dateFrom, dateTo);
  const [days, setDays] = useState<number>(DAY_OPTIONS.indexOf(viewDays) >= 0 ? viewDays : 15);
  const [sourceFrom, setSourceFrom] = useState('');
  const [targetFrom, setTargetFrom] = useState('');
  const [guideOpen, setGuideOpen] = useState(true);
  const [result, setResult] = useState<GeneratedOperations | null>(null);
  const [target, setTarget] = useState<BoardData | null>(null);
  const [excluded, setExcluded] = useState<Array<string>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const d = DAY_OPTIONS.indexOf(viewDays) >= 0 ? viewDays : 15;
      setDays(d);
      setSourceFrom(addDays(dateFrom, -d));
      setTargetFrom(dateFrom);
      setResult(null);
      setTarget(null);
      setExcluded([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dateFrom, dateTo]);

  const sourceTo = sourceFrom ? addDays(sourceFrom, days - 1) : '';
  const targetTo = targetFrom ? addDays(targetFrom, days - 1) : '';
  const ready = !!sourceFrom && !!targetFrom;

  const runPreview = async () => {
    if (!ready) {
      setError('コピーする期間と適用する期間を選んでください。');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // 既存シフトは反映先のシフト表から取る。反映予定は A9 が返す操作列
      const [board, copied] = await Promise.all([
        getBoard(orgCode, targetFrom, targetTo),
        copyShifts({
          orgCode, sourceFrom, sourceTo, targetFrom, staffCodes: [], overwrite: true,
        }),
      ]);
      setTarget(board);
      setResult(copied);
      setExcluded([]);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message) || 'プレビューを取得できませんでした。');
    } finally {
      setBusy(false);
    }
  };

  // 反映予定（セルごと。1 日に複数あれば全部。10/7 以前は最後の 1 件だけを覚えていて、プレビューに最後のシフトしか出なかった）
  const planned: { [key: string]: Array<Operation> } = {};
  if (result) {
    result.operations.forEach((op) => {
      if (op.staffCode && op.targetDate) {
        const k = cellKey(op.staffCode, op.targetDate);
        planned[k] = [...(planned[k] || []), op];
      }
    });
  }
  // 既存（セルごと。自店のシフトだけ。1 日に複数あれば全部）
  const existing: { [key: string]: Array<ShiftItem> } = {};
  if (target) {
    target.shifts.forEach((s) => {
      if (!s.isAway) {
        const k = cellKey(s.staffCode, s.targetDate);
        existing[k] = [...(existing[k] || []), s];
      }
    });
  }

  const dates: Array<string> = [];
  if (targetFrom && result) {
    for (let i = 0; i < days; i += 1) {
      dates.push(addDays(targetFrom, i));
    }
  }
  // 反映予定か既存がある人だけ出す。全員分出すと横にも縦にも長くなりすぎる
  const rows = (target ? target.staffs : staffs).filter((st) => dates.some(
    (d) => planned[cellKey(st.staffCode, d)] || existing[cellKey(st.staffCode, d)],
  ));
  // スタッフが法定／所定を区別するか（公休の表示と、変わったかどうかの判定に使う。10/6）。一覧に無い人は従来どおり区別する扱い
  const distinction: { [staffCode: string]: boolean } = {};
  (target ? target.staffs : staffs).forEach((st) => {
    distinction[st.staffCode] = !!st.distinctionHoliday;
  });
  const distinguishOf = (staffCode: string | null | undefined): boolean => (
    staffCode && distinction[staffCode] !== undefined ? distinction[staffCode] : true
  );

  // セルの既存と反映予定の文言（開始時刻順）。変わったかどうかはセル単位で比べる
  const beforeLabelsOf = (key: string, dist: boolean): Array<string> => (existing[key] || [])
    .map((x) => shiftLabel(x, dist)).filter((t) => !!t).sort();
  const afterLabelsOf = (key: string, dist: boolean): Array<string> => (planned[key] || [])
    .map((op) => opLabel(op, dist)).filter((t) => !!t).sort();
  const cellChanged = (key: string, staffCode: string | null | undefined): boolean => {
    if (!planned[key]) {
      return false;
    }
    const dist = distinguishOf(staffCode);
    return beforeLabelsOf(key, dist).join('|') !== afterLabelsOf(key, dist).join('|');
  };

  const toggle = (key: string, changed: boolean) => {
    if (!changed) {
      return;
    }
    setExcluded(excluded.indexOf(key) === -1 ? [...excluded, key] : excluded.filter((k) => k !== key));
  };

  // 変化のあるセルだけ積む。一致しているセルは積んでも意味がない
  // 変わるセルの操作は、そのセルの分を全部積む（同じ日の 2 件目以降も）
  const selected = result ? result.operations.filter((op) => {
    const key = cellKey(op.staffCode || '', op.targetDate || '');
    return excluded.indexOf(key) === -1 && cellChanged(key, op.staffCode);
  }) : [];

  const changedCount = result ? result.operations.filter((op) => cellChanged(cellKey(op.staffCode || '', op.targetDate || ''), op.staffCode)).length : 0;

  return (
    <Modal
      open={open}
      closeHandler={onClose}
      title="前期間からコピー"
      submitText={result ? `選んだ ${selected.length} 件をシフト表に取り込む` : 'コピー内容を確認'}
      onSubmit={() => {
        if (result) {
          onGenerated({ ...result, operations: selected, generated: selected.length });
          setResult(null);
        } else {
          runPreview();
        }
      }}
      // 取り込めるものが無いときは送信だけ止める（キャンセルは押せる）。通信中はキャンセルも止める（10/5）
      isDisabledAll={busy}
      isDisabledSubmit={!!result && selected.length === 0}
      // 共通の Modal は width を最小幅として使う。1180px 固定だと iPad mini で右が見切れていたので、画面より広くならないようにする（9/30）
      width="min(1180px, calc(100vw - 32px))"
      customStyle={css({ maxWidth: 'calc(100vw - 32px)' })}
    >
      <div style={{ minWidth: 0 }}>
        <div css={styles.section}>コピー対象期間を選択</div>
        <div css={styles.panel}>
          <div style={{ marginBottom: '14px' }}>
            <label css={styles.label} htmlFor="copy-days">コピー日数を選択</label>
            {/* ほかの画面と同じ共通の Select（10/7） */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BoardSelect
                id="copy-days"
                value={String(days)}
                onChange={(v) => { setDays(parseInt(v, 10)); setResult(null); }}
                options={DAY_OPTIONS.map((d) => ({ value: String(d), label: String(d) }))}
                style={{ width: '96px' }}
              />
              <span style={{ fontSize: '13px', marginLeft: '8px' }}>日間</span>
            </div>
          </div>
          <div css={styles.row}>
            <div>
              <label css={styles.label} htmlFor="copy-source">コピーする期間を選択</label>
              {/* 月次帳票などと同じカレンダー（10/6） */}
              <BoardDatePicker id="copy-source" value={sourceFrom} height={34} inModal={true} onChange={(v) => { setSourceFrom(v); setResult(null); }} />
            </div>
            <span css={styles.arrow}>❯</span>
            <div>
              <label css={styles.label} htmlFor="copy-target">適用する期間を選択</label>
              <BoardDatePicker id="copy-target" value={targetFrom} height={34} inModal={true} onChange={(v) => { setTargetFrom(v); setResult(null); }} />
            </div>
            <button type="button" css={styles.previewButton} onClick={runPreview} disabled={busy || !ready}>
              {busy ? '読み込み中…' : 'プレビューを見る'}
            </button>
          </div>
          {ready && (
            <div style={{ fontSize: '11px', color: mock.textSub, marginTop: '10px' }}>
              {`${sourceFrom} 〜 ${sourceTo} を ${targetFrom} 〜 ${targetTo} に反映します`}
            </div>
          )}
        </div>

        <button type="button" css={styles.guideToggle} onClick={() => setGuideOpen(!guideOpen)} aria-expanded={guideOpen}>
          <span>プレビュー内の表示の見方</span>
          <Icon type={guideOpen ? 'chevronUp' : 'chevronDown'} color={mock.primary} size="16px" verticalAlign="middle" />
        </button>
        {guideOpen && (
          <React.Fragment>
            <div css={styles.guideLead}>
              上段に
              <strong>「既存シフト」</strong>
              下段に
              <strong>「反映予定シフト」</strong>
              が表示されます。
            </div>
            <div css={styles.guideCards}>
              <div css={styles.guideCard}>
                <div css={styles.guideTitle}>
                  既存シフトと反映予定シフトの
                  <br />
                  勤務時間が「一致している」場合
                </div>
                <div css={styles.guideBody}>
                  <div css={styles.guideNote}>背景は白表示となり反映予定シフトは表示されません</div>
                  <div css={styles.sample} style={{ background: SAME_BG }}>
                    <span css={styles.before}>13:00-21:00</span>
                  </div>
                </div>
              </div>
              <div css={styles.guideCard}>
                <div css={styles.guideTitle}>
                  既存シフトと反映予定シフトの
                  <br />
                  勤務時間が「異なる」場合
                </div>
                <div css={styles.guideBody}>
                  <div css={styles.guideNote}>背景は黄表示となり既存シフトと反映予定シフトが表示されます</div>
                  <div css={styles.sample} style={{ background: DIFF_BG }}>
                    <span css={styles.before}>13:00-21:00</span>
                    <span css={styles.down}>▼</span>
                    <span css={styles.after}>13:00-18:00</span>
                  </div>
                </div>
              </div>
              <div css={styles.guideCard}>
                <div css={styles.guideTitle}>
                  反映予定シフトを「適用しない」場合
                </div>
                <div css={styles.guideBody}>
                  <div css={styles.guideNote}>背景が黄表示の際に選択すると背景がグレー表示になり反映予定のシフトは適用されません</div>
                  <div css={styles.sample} style={{ background: OFF_BG }}>
                    <span css={styles.before}>13:00-21:00</span>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        )}

        {error && <div style={{ color: mock.secondary, fontSize: '12px', marginBottom: '8px' }}>{error}</div>}

        {result && (
          <React.Fragment>
            <div css={styles.summary}>
              <span>{`変わるセル ${changedCount} 件`}</span>
              <span>{`適用しない ${excluded.length} 件`}</span>
              <span style={{ color: mock.textSub }}>{`飛ばす ${result.skipped.length} 件`}</span>
              <button
                type="button"
                css={styles.resetButton}
                onClick={() => setExcluded([])}
                disabled={excluded.length === 0}
                title="「適用しない」にしたセルをすべて元に戻します"
              >
                初期状態に戻す
              </button>
              <span style={{ color: mock.textSub, marginLeft: 'auto' }}>黄色のセルを押すと、その 1 件だけ適用しない扱いになります</span>
            </div>
            <div css={styles.grid}>
              <div css={styles.gridRow}>
                <div
                  css={styles.nameCell}
                  style={{
                    background: mock.primaryM95, position: 'sticky', top: 0, zIndex: 3,
                  }}
                >
                  スタッフ
                </div>
                {dates.map((d) => {
                  const dd = parseDateStr(d);
                  return (
                    <div key={d} css={styles.headCell}>
                      <div>{`${dd.getMonth() + 1}/${dd.getDate()}`}</div>
                      <div style={{ fontSize: '10px', color: dowColor(dd.getDay()) }}>
                        {DOW_LABELS[dd.getDay()]}
                      </div>
                    </div>
                  );
                })}
              </div>
              {rows.map((st) => (
                <div key={st.staffCode} css={styles.gridRow}>
                  <div css={styles.nameCell} title={st.staffName}>{st.staffName}</div>
                  {dates.map((d) => {
                    const key = cellKey(st.staffCode, d);
                    const dist = distinguishOf(st.staffCode);
                    const beforeLabels = beforeLabelsOf(key, dist);
                    const afterLabels = afterLabelsOf(key, dist);
                    const changed = cellChanged(key, st.staffCode);
                    const off = excluded.indexOf(key) !== -1;
                    const bg = cellBackground(changed, off);
                    return (
                      <div
                        key={d}
                        css={styles.cell}
                        style={{ background: bg, cursor: changed ? 'pointer' : 'default' }}
                        onClick={() => toggle(key, changed)}
                        title={cellHint(changed, off)}
                      >
                        {beforeLabels.map((t) => <span key={`b-${t}`} css={styles.before}>{t}</span>)}
                        {changed && !off && (
                          <React.Fragment>
                            {beforeLabels.length > 0 && <span css={styles.down}>▼</span>}
                            {afterLabels.map((t) => <span key={`a-${t}`} css={styles.after}>{t}</span>)}
                          </React.Fragment>
                        )}
                        {changed && off && beforeLabels.length === 0 && <span css={styles.before}>—</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
              {rows.length === 0 && (
                <div style={{ padding: '20px', fontSize: '12px', color: mock.textSub }}>コピーできるシフトがありません。</div>
              )}
            </div>
            {result.skipped.length > 0 && (
              <div css={styles.skips}>
                {result.skipped.slice(0, 200).map((s) => (
                  <div key={`${s.staffCode}-${s.targetDate}`}>
                    {`${s.targetDate} ${(staffs.filter((x) => x.staffCode === s.staffCode)[0] || { staffName: s.staffCode }).staffName}: ${SKIP_LABEL[s.reason] || s.reason}`}
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default CopyModal;
